import {
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  MenuItem,
  Select,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { ApexOptions } from "apexcharts";
import {
  convertESTtoUserLocalTime,
  handleErrorMessage,
  isTruthy,
} from "helpers/methods";
import ReactApexChart from "react-apexcharts";
import moment from "moment";
import { useEffect, useState } from "react";
import CustomInfoTooltip from "global/components/CustomInfoTooltip/CustomInfoTooltip";
import { getCampaignsPerformanceData } from "screens/Campaigns/CampaignService";

interface CustomProps {
  campaignId: number;
  campaignScheduleDate?: any;
  campaign?: any;
}

const PerformanceChart = (props: CustomProps) => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("sm"));
  const [open, setOpen] = useState<boolean>(false);
  const [selectedDays, setSelectedDays] = useState<string[]>([
    moment(props.campaignScheduleDate, "YYYY-MM-DDTHH:mm:ss").format(
      "YYYY-MM-DD"
    ),
  ]);
  const [isSelectDisabled, setIsSelectDisabled] = useState<boolean>(true);
  const [allPerformanceData, setAllPerformanceData] = useState<{
    [key: string]: { x: string; y: number }[];
  }>({});
  const [visibleLines, setVisibleLines] = useState<{
    opened: boolean;
    clicked: boolean;
  }>({
    opened: true,
    clicked: false,
  });

  const [xAxisCategories, setXAxisCategories] = useState<string[]>([]);
  const [initialApiCalled, setInitialApiCalled] = useState<boolean>(false);

  useEffect(() => {
    if (props.campaignId && isTruthy(props.campaignScheduleDate)) {
      getCampaignPerformanceData();
    }
  }, [props.campaignId, visibleLines]);

  useEffect(() => {
    if (initialApiCalled) {
      const availableDates = generateAvailableDates();
      if (availableDates.length > 1) {
        fetchAllPerformanceData();
      } else {
        setIsSelectDisabled(true);
      }
    }
  }, [initialApiCalled]);

  const fetchAllPerformanceData = async () => {
    try {
      const generatedDates = generateAvailableDates();
      const allDates = generatedDates
        .filter((date) => {
          return (
            date !==
            moment(props.campaignScheduleDate, "YYYY-MM-DDTHH:mm:ss").format(
              "MM-DD-YYYY"
            )
          );
        })
        .map((date) => moment(date, "MM-DD-YYYY").format("YYYY-MM-DD"));
      if (allDates.length) {
        const response = await getCampaignsPerformanceData(props.campaignId, {
          dates: allDates,
        });
        const combinedData = transformDataForApex(response);
        setAllPerformanceData({ ...allPerformanceData, ...combinedData });
        setIsSelectDisabled(false);
        setInitialApiCalled(false);
      }
    } catch (error: any) {
      setIsSelectDisabled(false);
      handleErrorMessage(error);
    }
  };

  const generateAvailableDates = () => {
    const dates: string[] = [];
    const today = moment().startOf("day");
    const scheduleDate = moment(
      props.campaignScheduleDate,
      "YYYY-MM-DDTHH:mm:ss"
    ).startOf("day");

    for (let i = 0; i < 7; i++) {
      const nextDate = scheduleDate.clone().add(i, "days");
      if (nextDate.isAfter(today)) break;
      dates.push(nextDate.format("MM-DD-YYYY"));
    }
    return dates;
  };

  const getCampaignPerformanceData = async () => {
    try {
      const response = await getCampaignsPerformanceData(props.campaignId, {
        dates: [
          moment(props.campaignScheduleDate, "YYYY-MM-DDTHH:mm:ss").format(
            "YYYY-MM-DD"
          ),
        ],
      });
      const formattedData = transformDataForApex(response);
      setAllPerformanceData({ ...allPerformanceData, ...formattedData });
      setIsSelectDisabled(true);
      setInitialApiCalled(true);
    } catch (error: any) {
      handleErrorMessage(error);
    }
  };

  const handleDateChange = (e: any) => {
    const { name, checked } = e.target;
    const date = moment(name, "MM-DD-YYYY").format("YYYY-MM-DD");
    let updatedSelectedDays = checked
      ? [...selectedDays, date]
      : selectedDays.filter((day) => day !== date);
    setSelectedDays(updatedSelectedDays);
  };

  const transformDataForApex = (response: any) => {
    const categories: string[] = [];
    const allData: { [key: string]: { x: string; y: number }[] } = {};
    Object.keys(response).forEach((date) => {
      const datesData: { x: string; y: number }[] = [];
      const filteredResponse = response[date].filter(
        (_: any, index: number) => index % 2 === 0
      );
      filteredResponse.forEach((item: any) => {
        // Convert timestamp to formatted time (hh:mm A)
        const formattedTime = moment(item.Time, "MM/DD/YYYY HH:mm:ss").format(
          "hh:mm A"
        );
        if (!categories.includes(formattedTime)) categories.push(formattedTime);
        let total = 0;
        if (visibleLines.opened) {
          total += item.Opened || 0;
        }
        if (visibleLines.clicked) {
          total += item.Clicked || 0;
        }
        datesData.push({ x: formattedTime, y: total });
      });
      allData[date] = datesData;
    });
    setXAxisCategories(categories);
    return allData;
  };

  const getSeries = () => {
    const series = selectedDays.map((day: any) => {
      return { name: day, data: allPerformanceData[day] };
    });
    return series;
  };

  return (
    <>
      <Stack
        direction={isDesktop ? "row" : "column"}
        justifyContent="space-between"
        alignItems={isDesktop ? "center" : "flex-start"}
        spacing={1}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="subtitle1" fontSize="20px" fontWeight="600">
            Performance Report
          </Typography>
          <CustomInfoTooltip
            placement="top"
            infoMessage={
              "This graph illustrates the count of opens and clicks over the selected interval."
            }
          />
        </Stack>
        <Stack direction="row" alignItems="center !important" spacing={1}>
          <FormGroup row>
            <FormControlLabel
              control={
                <Checkbox
                  checked={visibleLines.opened}
                  onChange={() =>
                    setVisibleLines({
                      ...visibleLines,
                      opened: !visibleLines.opened,
                    })
                  }
                  sx={{
                    "&.Mui-checked": { color: theme.palette.primary.main },
                  }}
                  disabled={isSelectDisabled}
                />
              }
              label="Opened"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={visibleLines.clicked}
                  onChange={() =>
                    setVisibleLines({
                      ...visibleLines,
                      clicked: !visibleLines.clicked,
                    })
                  }
                  sx={{
                    "&.Mui-checked": { color: theme.palette.primary.main },
                  }}
                  disabled={
                    (props.campaign?.campaignCounts?.find(
                      (item: any) => item.metricId === 6
                    )?.count || 0) === 0
                  }
                />
              }
              label="Clicked"
            />
          </FormGroup>
          <Select
            multiple
            value={selectedDays}
            displayEmpty
            renderValue={() =>
              selectedDays.length > 0
                ? selectedDays
                    .sort(
                      (a, b) =>
                        moment(a, "YYYY-MM-DD").toDate().getTime() -
                        moment(b, "YYYY-MM-DD").toDate().getTime()
                    )
                    .map((date) => moment(date, "YYYY-MM-DD").format("MMM Do"))
                    .join(", ")
                : "Date"
            }
            open={open}
            onClose={() => setOpen(false)}
            onOpen={() => setOpen(true)}
            MenuProps={{
              PaperProps: {
                style: {
                  maxHeight: 200,
                  overflowY: "auto",
                },
              },
            }}
          >
            <FormControl>
              <FormGroup>
                {isTruthy(props.campaignScheduleDate) &&
                  generateAvailableDates()?.map((date) => (
                    <MenuItem key={date} value={date}>
                      <FormControlLabel
                        sx={{
                          width: "100%",
                          margin: "0px",
                        }}
                        label={convertESTtoUserLocalTime(date, "", "MMM Do")}
                        name={date}
                        control={
                          <Checkbox
                            onChange={(e: any) => handleDateChange(e)}
                            checked={selectedDays.includes(
                              convertESTtoUserLocalTime(date, "", "YYYY-MM-DD")
                            )}
                            sx={{
                              "&.Mui-checked": {
                                color: theme.palette.primary.main,
                              },
                            }}
                          />
                        }
                      />
                    </MenuItem>
                  ))}
              </FormGroup>
            </FormControl>
          </Select>
        </Stack>
      </Stack>
      <ReactApexChart
        options={
          {
            chart: {
              type: "line" as const,
              zoom: { enabled: false },
              toolbar: { show: false },
            },
            stroke: {
              width: 2, // ✅ Adjust line width
              curve: "smooth",
            },
            xaxis: {
              type: "category" as const, // ✅ Ensure category format
              categories: xAxisCategories,
              labels: {
                rotate: -45, // ✅ Improve visibility
              },
            },
            yaxis: {
              labels: { formatter: (val) => Math.round(val).toString() },
            },
            tooltip: {
              x: {
                formatter: (val: number) => val.toString(), // ✅ Show formatted time in tooltip
              },
            },
            legend: {
              show: true, // ✅ Always show legend, even if only one line is present
              position: "bottom", // Optional: Adjust legend position
              markers: {
                radius: 2, // ✅ Improve marker visibility
              },
            },
            colors: [], // ✅ Purple for Opened, Green for Clicked
          } as ApexOptions
        }
        series={getSeries()}
        type="line"
        height={300}
      />
    </>
  );
};

export default PerformanceChart;
